package fr.frux.resources

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.InternalResourceApi
import org.jetbrains.compose.resources.ResourceReader

/**
 * Ce que [RetryingResourceReader] attend de la source qu'il enveloppe : la surface de
 * [ResourceReader], sans son opt-in interne, pour que les tests puissent fournir une source
 * factice sans dépendre de l'API de Compose Resources.
 */
interface ResourceBytesSource {
    suspend fun read(path: String): ByteArray

    suspend fun readPart(
        path: String,
        offset: Long,
        size: Long,
    ): ByteArray

    fun getUri(path: String): String
}

/**
 * Lecteur de ressources qui rejoue une lecture qui a échoué (#619).
 *
 * Sur le web, une ressource Compose (fonte, image) se charge par `fetch`. Un réseau mobile qui
 * lâche pendant la lecture fait remonter « The I/O read operation failed » dans une coroutine que
 * personne ne rattrape, et l'écran attend une fonte qui n'arrivera jamais. Deux nouveaux essais
 * espacés de [delayMillis] absorbent une coupure courte. Au-delà, l'erreur d'origine remonte,
 * telle quelle, pour que Sentry continue de la compter.
 *
 * L'annulation n'est jamais rejouée : une composition qui quitte l'écran annule sa lecture, et
 * c'est le comportement attendu.
 */
class RetryingResourceReader(
    private val delegate: ResourceBytesSource,
    private val attempts: Int = DEFAULT_ATTEMPTS,
    private val delayMillis: Long = DEFAULT_DELAY_MILLIS,
) : ResourceBytesSource {
    init {
        require(attempts >= 1) { "attempts doit valoir au moins 1" }
    }

    override suspend fun read(path: String): ByteArray = retrying { delegate.read(path) }

    override suspend fun readPart(
        path: String,
        offset: Long,
        size: Long,
    ): ByteArray = retrying { delegate.readPart(path, offset, size) }

    override fun getUri(path: String): String = delegate.getUri(path)

    // Une lecture par fetch échoue avec une erreur du navigateur (TypeError, DOMException) : sur
    // Kotlin/JS c'est un Throwable qui n'est pas une Exception Kotlin, comme les erreurs du moteur
    // Ktor (#121). Un `catch (e: Exception)` la laissait passer sans nouvel essai (FRUX-WEB-A).
    // Tout ce qui n'est pas une annulation est un échec de lecture à rejouer.
    @Suppress("TooGenericExceptionCaught")
    private suspend fun <T> retrying(block: suspend () -> T): T {
        var remaining = attempts
        while (true) {
            try {
                return block()
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                remaining--
                if (remaining <= 0) throw e
                delay(delayMillis)
            }
        }
    }

    companion object {
        const val DEFAULT_ATTEMPTS = 3
        const val DEFAULT_DELAY_MILLIS = 300L
    }
}

/** Enveloppe un [ResourceReader] de Compose Resources dans la surface neutre [ResourceBytesSource]. */
@OptIn(InternalResourceApi::class, ExperimentalResourceApi::class)
fun ResourceReader.asBytesSource(): ResourceBytesSource =
    object : ResourceBytesSource {
        override suspend fun read(path: String): ByteArray = this@asBytesSource.read(path)

        override suspend fun readPart(
            path: String,
            offset: Long,
            size: Long,
        ): ByteArray = this@asBytesSource.readPart(path, offset, size)

        override fun getUri(path: String): String = this@asBytesSource.getUri(path)
    }

/** Présente un [ResourceBytesSource] sous l'interface que Compose Resources consomme. */
@OptIn(InternalResourceApi::class, ExperimentalResourceApi::class)
fun ResourceBytesSource.asResourceReader(): ResourceReader =
    object : ResourceReader {
        override suspend fun read(path: String): ByteArray = this@asResourceReader.read(path)

        override suspend fun readPart(
            path: String,
            offset: Long,
            size: Long,
        ): ByteArray = this@asResourceReader.readPart(path, offset, size)

        override fun getUri(path: String): String = this@asResourceReader.getUri(path)
    }
